import { message, Button, Modal, Tooltip } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import apiClient from '@api-client';
import { useAdminDocumentController_findOneById } from '@api-client/generated/AdminDocumentController/findOneById';
import { useAdminDocumentController_updateOneById } from '@api-client/generated/AdminDocumentController/updateOneById';
import { CopyButton, Loader } from '@components';
import {
  DetailsHeader,
  DocumentExpenseCard,
  DocumentExpenseReport,
  FilePreview,
} from '@entities';
import { useCurrentCompanyId } from '@hooks';
import { IconArrowLeft, IconArrowRight } from '@assets';
import {
  getDocumentExpenseDetailsPath,
  getDocumentNavigation,
  getNextPage,
  isPreviewPending,
  PREVIEW_POLL_INTERVAL_MS,
  type DocumentListNavigationState,
} from '@utils';

import * as S from './styled';

const PREVIOUS_DOCUMENT_LABEL = 'Previous';
const NEXT_DOCUMENT_LABEL = 'Next';

const DocumentExpenseDetails: FC = () => {
  const { id: documentId } = useParams();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const navState = routerLocation.state as DocumentListNavigationState | null;
  const fromSearch = navState?.fromSearch || '';
  const documentIds = navState?.documentIds;
  const documentQuery = navState?.documentQuery;
  const nextPage = navState?.nextPage ?? null;

  const { prevId, nextId } = getDocumentNavigation(documentIds, documentId);

  const [isLoadingNextPage, setIsLoadingNextPage] = useState(false);
  // Mirrors the expense form's unsaved/in-flight state so booking stays
  // disabled until edits are persisted — otherwise the post-booking refetch
  // would overwrite the form and lose not-yet-saved input.
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Next stays available either within the loaded ids or by fetching the next
  // page from the API, so the loaded-window edge is not mistaken for the end.
  const canLoadMore = nextPage !== null && Boolean(documentQuery);
  const canGoNext = Boolean(nextId) || canLoadMore;

  const companyId = useCurrentCompanyId();
  const queryClient = useQueryClient();
  const [modal, contextHolder] = Modal.useModal();

  // Builds the nav state carried with each prev/next hop from the current
  // context, so adding a field doesn't require updating every call site.
  const navStateWith = (
    overrides: Partial<DocumentListNavigationState> = {}
  ): DocumentListNavigationState => ({
    fromSearch,
    documentIds,
    documentQuery,
    nextPage,
    ...overrides,
  });

  // Keep the list context (filters, ordered ids, cursor) while stepping through
  // documents, so prev/next stays consistent with the operator's filtered list.
  const goToDocument = (
    id: string,
    state: DocumentListNavigationState = navStateWith()
  ) => navigate(getDocumentExpenseDetailsPath(companyId!, id), { state });

  // At the end of the loaded ids, pull the next page so navigation continues
  // across the full filtered list rather than stopping at what was scrolled.
  const goToNextDocument = async () => {
    if (nextId) {
      goToDocument(nextId);
      return;
    }

    if (nextPage === null || !documentQuery) {
      return;
    }

    setIsLoadingNextPage(true);

    try {
      const response = await apiClient.AdminDocumentController_findAll({
        parameter: { ...documentQuery, page: nextPage },
      });

      const knownIds = new Set(documentIds ?? []);
      // Skip ids the list already had: offset paging can re-serve a document if
      // the underlying list shifted, and duplicates would make prev/next jump
      // back to the earlier occurrence.
      const freshIds = (response.records ?? []).flatMap((record) =>
        knownIds.has(record.id) ? [] : [record.id]
      );
      const followingPage = getNextPage(
        response.metadata.currentPage,
        response.metadata.totalPages
      );

      if (!freshIds.length) {
        // Nothing new on this page (list shrank/shifted). Advance the cursor in
        // place so the next click moves on instead of re-fetching this page,
        // and "Next" disables once the cursor runs past the last page.
        navigate(getDocumentExpenseDetailsPath(companyId!, documentId!), {
          replace: true,
          state: navStateWith({ nextPage: followingPage }),
        });
        return;
      }

      goToDocument(freshIds[0], {
        ...navStateWith(),
        documentIds: [...(documentIds ?? []), ...freshIds],
        nextPage: followingPage,
      });
    } catch {
      message.error('Failed to load the next document');
    } finally {
      setIsLoadingNextPage(false);
    }
  };

  const [isVisibleReportError, setIsVisibleReportError] = useState(false);

  const {
    data: document,
    isFetching: isLoading,
    refetch,
  } = useAdminDocumentController_findOneById({
    params: {
      id: documentId!,
      companyId: companyId!,
    },
    config: {
      refetchOnWindowFocus: false,
      // Poll while the HEIC→JPEG preview is still generating so it appears without
      // a manual reload; stop once it is ready/failed.
      refetchInterval: (query) =>
        isPreviewPending(query.state.data) ? PREVIEW_POLL_INTERVAL_MS : false,
    },
  });

  const { mutate: updateDocument } = useAdminDocumentController_updateOneById();

  const isBooked = document?.adminStatus === 'processed';
  // While a document is being processed (automatic or manual booking) the form
  // itself owns the booking actions, so the header action is hidden to avoid
  // two different ways to reach the same 'processed' status.
  const isProcessing = document?.adminStatus === 'processing';

  // Marks the current document as booked, mirroring the list-screen action.
  // On success we refetch this document (so the form, incl. the Doc ID field,
  // re-renders with its booked behaviour) and invalidate the documents list
  // query, so the related list reflects the new status when it is revisited.
  const handleMarkAsBooked = () =>
    modal.confirm({
      title: 'Are you sure you want to mark as booked this file?',
      okButtonProps: { size: 'large' },
      cancelButtonProps: { size: 'large' },
      icon: null,
      onOk: () =>
        updateDocument(
          {
            parameter: {
              id: documentId!,
              companyId: companyId!,
            },
            requestBody: {
              adminStatus: 'processed',
            },
          },
          {
            onSuccess: () => {
              message.success('The document has been marked as booked');
              refetch();
              queryClient.invalidateQueries({
                queryKey: ['AdminDocumentController_findAll'],
              });
            },
            // Surface booking failures: the modal has already closed, so without
            // this the operator gets no signal and may leave believing the
            // document is booked when it is not.
            onError: () => {
              message.error('Failed to mark the document as booked');
            },
          }
        ),
    });

  const handleSendReport = (errorCode: string) =>
    updateDocument(
      {
        parameter: {
          id: documentId!,
          companyId: companyId!,
        },
        requestBody: {
          adminStatus: 'requested_changes',
          errorCode,
        },
      },
      {
        onSuccess: () => {
          message.success('The error report has been sent successfully');
        },
      }
    );

  if (!document || isLoading) {
    return <Loader />;
  }

  return (
    <>
      <DetailsHeader
        goBackText="Go to all documents"
        onNavigate={() =>
          navigate(`/companies/${companyId}/documents${fromSearch}` , { replace: true })
        }
        extra={
          <S.Navigation>
            <S.NavButton
              size="large"
              icon={<IconArrowLeft />}
              disabled={!prevId}
              onClick={() => prevId && goToDocument(prevId)}
            >
              {PREVIOUS_DOCUMENT_LABEL}
            </S.NavButton>
            <S.NavButton
              size="large"
              loading={isLoadingNextPage}
              disabled={!canGoNext}
              onClick={goToNextDocument}
            >
              {NEXT_DOCUMENT_LABEL}
              <IconArrowRight />
            </S.NavButton>
          </S.Navigation>
        }
      >
        <CopyButton
          tooltipPlacement="bottom"
          icon="chain"
          value={window.location.href}
          type="link"
        />
        {!isProcessing && (
          <Tooltip
            title={
              hasUnsavedChanges && !isBooked
                ? 'Wait for changes to be saved'
                : ''
            }
          >
            {/* Wrapped in a span so the tooltip still appears on hover while the
                button is disabled — a disabled button receives no mouse events,
                so the tooltip would otherwise never trigger. */}
            <span>
              <Button
                type="primary"
                size="small"
                disabled={isBooked || hasUnsavedChanges}
                onClick={handleMarkAsBooked}
                style={{
                  pointerEvents:
                    isBooked || hasUnsavedChanges ? 'none' : undefined,
                }}
              >
                Mark as Booked
              </Button>
            </span>
          </Tooltip>
        )}
      </DetailsHeader>

      {contextHolder}

      <DocumentExpenseReport
        open={isVisibleReportError}
        onRequest={handleSendReport}
        onCancel={() => setIsVisibleReportError(false)}
      />

      <S.Inner gap={30} align="flex-start">
        <FilePreview
          // NOTE: This functionality is hidden for now
          // headerContent={
          //   <Flex justify="flex-end">
          //     <S.ReportLink
          //       disabled={document.adminStatus === 'processed'}
          //       onClick={() => setIsVisibleReportError(true)}
          //     >
          //       <IconWarningSquare /> Report error
          //     </S.ReportLink>
          //   </Flex>
          // }
          variant="a4"
          file={document}
        />

        <DocumentExpenseCard
          document={document}
          onAfterUpdate={refetch}
          onPendingChange={setHasUnsavedChanges}
        />
      </S.Inner>
    </>
  );
};

export default DocumentExpenseDetails;
